import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import {
  ActivitiesService,
  ApiError,
  CapacityService,
  OrganisationSettingsService,
} from "@/api";
import { useAuthContext } from "@/contexts/AuthContext";
import type {
  AttendanceDayGroup,
  AttendanceGroup,
  PresentTodayData,
} from "@/types/attendance";
import type {
  ChildCurrentStateResponse,
  ChildSummaryDTO,
} from "@/types/activities";
import type { Childcare } from "@/types/organisation";
import {
  orgWallClockToISO,
  setOrgTimezone,
  toOrgDateOnly,
} from "@/utils/dateUtils";
import { useOrgTimezoneQuery } from "@/hooks/settings/useOrgTimezone";

export const presentTodayKeys = {
  all: ["present-today"] as const,
  day: (orgId: string | null | undefined, date: string) =>
    ["present-today", orgId, date] as const,
};

// Missing or ambiguous placement stays visible without inventing a group.
export function buildAttendanceGroups(
  dayGroups: AttendanceDayGroup[],
  arrivals: ChildSummaryDTO[],
  states: Record<string, ChildCurrentStateResponse>,
): AttendanceGroup[] {
  const groups = new Map<string, AttendanceGroup>();
  const children = new Map<
    string,
    { child: ChildSummaryDTO; groups: Set<string>; absent: boolean }
  >();
  for (const group of dayGroups) {
    if (!group.group_id) continue;
    groups.set(group.group_id, {
      id: group.group_id,
      name: group.group_name ?? "",
      childcareId: group.childcare_id ?? "",
      children: [],
    });
    for (const alloc of group.allocations ?? []) {
      if (!alloc.child_id) continue;
      const existing = children.get(alloc.child_id);
      const target =
        alloc.has_override && alloc.override_group_id
          ? alloc.override_group_id
          : group.group_id;
      const placements = existing?.groups ?? new Set<string>();
      placements.add(target);
      children.set(alloc.child_id, {
        child: {
          id: alloc.child_id,
          firstname: alloc.child_firstname,
          lastname: alloc.child_lastname,
        },
        groups: placements,
        absent: (existing?.absent ?? true) && alloc.is_absent === true,
      });
    }
  }
  for (const child of arrivals) {
    if (child.id && !children.has(child.id)) {
      children.set(child.id, { child, groups: new Set(), absent: false });
    }
  }
  for (const [id, entry] of children) {
    const state = states[id];
    const bucket = state?.is_checked_in
      ? "present"
      : state?.check_in_time
        ? "checkedOut"
        : entry.absent
          ? "absent"
          : "expected";
    // An activity-only child belongs here only if they actually attended today.
    if (
      entry.groups.size === 0 &&
      bucket !== "present" &&
      bucket !== "checkedOut"
    )
      continue;
    const targetId = entry.groups.size === 1 ? [...entry.groups][0] : "";
    let group = groups.get(targetId);
    if (!group) {
      if (!groups.has(""))
        groups.set("", { id: "", name: "", childcareId: "", children: [] });
      group = groups.get("")!;
    }
    group.children.push({
      id,
      name: `${entry.child.firstname ?? ""} ${entry.child.lastname ?? ""}`.trim(),
      bucket,
      state,
    });
  }
  return [...groups.values()];
}

export async function fetchPresentToday(
  orgId: string,
  date: string,
): Promise<PresentTodayData> {
  const calendar = await CapacityService.getApiV1CapacityCalendarWeek(
    date,
    undefined,
    1,
  );
  const day = calendar.days?.find((item) => item.date === date);
  if (!day)
    throw new Error("Attendance calendar did not return the requested day");
  const arrivals: ChildSummaryDTO[] = [];
  // Include arrivals outside the planned roster. Page every check-in, never just
  // the first 100. Abort an incomplete response instead of reporting low totals.
  let fetched = 0;
  for (let page = 1; ; page++) {
    const response = await ActivitiesService.getApiV1Activities(
      page,
      100,
      undefined,
      "check_in",
      orgWallClockToISO(date, "00:00:00"),
      new Date(
        Date.parse(orgWallClockToISO(date, "23:59:59")!) + 999,
      ).toISOString(),
    );
    if (!Number.isInteger(response.total) || (response.total ?? -1) < 0)
      throw new Error("Attendance check-ins missing total");
    const items = response.items ?? [];
    fetched += items.length;
    for (const item of items) if (item.child?.id) arrivals.push(item.child);
    if (fetched >= response.total!) break;
    if (!items.length) throw new Error("Incomplete attendance check-ins");
  }
  const ids = [
    ...new Set(
      [
        ...(day.groups ?? []).flatMap((group) =>
          (group.allocations ?? []).map((child) => child.child_id),
        ),
        ...arrivals.map((child) => child.id),
      ].filter((id): id is string => !!id),
    ),
  ].sort();
  const states: Record<string, ChildCurrentStateResponse> = {};
  for (let offset = 0; offset < ids.length; offset += 500) {
    const response = await ActivitiesService.postApiV1ChildrenCurrentStates({
      child_ids: ids.slice(offset, offset + 500),
      date,
    });
    Object.assign(states, response.states ?? {});
  }
  const childcareNames: Record<string, string> = {};
  for (let offset = 0; ;) {
    const response =
      await OrganisationSettingsService.getApiV1OrganisationsChildcares(
        orgId,
        100,
        offset,
      );
    if (
      !Number.isInteger(response.totalCount) ||
      (response.totalCount ?? -1) < 0
    )
      throw new Error("Attendance childcares missing total");
    const childcares = (response.data ?? []) as Childcare[];
    for (const childcare of childcares)
      if (childcare.id) childcareNames[childcare.id] = childcare.name ?? "";
    offset += childcares.length;
    if (offset >= response.totalCount!) break;
    if (!childcares.length) throw new Error("Incomplete attendance childcares");
  }
  return {
    groups: buildAttendanceGroups(day.groups ?? [], arrivals, states),
    childcareNames,
  };
}

export function usePresentToday(enabled = true) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();
  // `toOrgDateOnly` reads the module-global zone that `useOrgTimezone` installs
  // from a query. Subscribing to the same cached query here is what makes the
  // effect below rerun when the organisation, and with it the zone, changes.
  const { data: orgTimezone } = useOrgTimezoneQuery();
  const [date, setDate] = useState(() =>
    toOrgDateOnly(new Date().toISOString()),
  );
  useEffect(() => {
    // `useOrgTimezone` sits near the root, and React runs a parent's effect
    // AFTER a child's. Installing the resolved zone here as well - the call is
    // idempotent and rejects an unusable name - keeps the query key on the new
    // organisation's calendar day in the same commit instead of up to 30
    // seconds later, on the previous organisation's date.
    setOrgTimezone(orgTimezone);
    const update = () => setDate(toOrgDateOnly(new Date().toISOString()));
    update();
    const timer = setInterval(update, 30_000);
    return () => clearInterval(timer);
  }, [orgId, orgTimezone]);
  const query = useQuery({
    queryKey: presentTodayKeys.day(orgId, date),
    queryFn: () => fetchPresentToday(orgId!, date),
    enabled: enabled && isSignedIn && isAuthReady && !!orgId,
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: (count, error) =>
      !(error instanceof ApiError && [401, 403].includes(error.status)) &&
      count < 2,
  });
  return { ...query, date };
}
